import React, { useEffect, useState } from 'react';
import {
  Box, Button, TextField, Typography, Chip, Stack,
  RadioGroup, Radio, FormControlLabel, TextareaAutosize, Autocomplete, Link
} from '@mui/material';
import { styled } from 'styled-components';
import axios from 'axios';
import BASE_API_URL from '../apis/index';
import countries from "../utils/countries";
import CompRgstrdFormSubmitted from './Modals/CompRgstrdFormSubmitted';

const serviceCatalogOptions = [
  "Clinical Monitoring", "Data Management", "Biostatistics",
  "Regulatory Affairs", "Medical Writing", "Pharmacovigilance",
  "Phase I-IV Trials", "Therapeutic Area Expertise"
];

const countryList = countries.map(c => c.label);

const UserCROServiceRegistrationForm = ({
  compId,
  compName,
  initialData,
  isEdit = false,
  serviceId
}) => {

  const [showRgstrdScsModal, setShowRgstrdScsModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [existingQmsDoc, setExistingQmsDoc] = useState([]);     // ✅ Existing file from DB
  const [newQmsFile, setNewQmsFile] = useState(null);           // ✅ New uploaded file

  const [formData, setFormData] = useState({
    serviceCatalog: [],
    geographicReach: [],
    employeeCount: "",
    qmsStatus: "no",
    complianceStatement: "",
    pastAuditDate: "",
    sopFollowing: "no"
  });

  const [customService, setCustomService] = useState("");

  // ✅ Prefill on initialData change
  useEffect(() => {
    if (!initialData) return;

    setFormData({
      serviceCatalog: initialData.serviceCatalog || [],
      geographicReach: initialData.geographicReach || [],
      employeeCount: initialData.employeeCount || "",
      qmsStatus: initialData.qmsStatus || "no",
      complianceStatement: initialData.complianceStatement || "",
      pastAuditDate: initialData.pastAuditDate || "",
      sopFollowing: initialData.sopFollowing || "no"
    });

    // ✅ Prefill existing file
    setExistingQmsDoc(initialData.qmsFile || []);
  }, [initialData]);

  // ✅ Toggle service catalog
  const toggleServiceCatalog = (option) => {
    setFormData(prev => ({
      ...prev,
      serviceCatalog: prev.serviceCatalog.includes(option)
        ? prev.serviceCatalog.filter(o => o !== option)
        : [...prev.serviceCatalog, option]
    }));
  };

  // ✅ Add custom catalog
  const addCustomCatalogService = () => {
    const trimmed = customService.trim();
    if (trimmed && !formData.serviceCatalog.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        serviceCatalog: [...prev.serviceCatalog.filter(o => o !== "Other"), trimmed]
      }));
      setCustomService("");
    }
  };

  // ✅ QMS file upload
  const handleQmsFileChange = (e) => {
    setNewQmsFile(e.target.files[0]);
  };

  /** ✅ Validation */
  const validateForm = () => {
    if (!formData.serviceCatalog.length) return "Select at least one Service Catalog";
    if (!formData.geographicReach.length) return "Select at least one Geographic Reach Country";

    if (formData.qmsStatus === "yes") {
      if (!existingQmsDoc.length && !newQmsFile)
        return "Please attach the QMS Document file";
    }

    return null;
  };

  /** ✅ Submit */
  const handleSubmit = async () => {
    const errorMsg = validateForm();
    if (errorMsg) return alert(errorMsg);

    try {
      setLoading(true);
      const authToken = localStorage.getItem("authToken");
      const submissionData = new FormData();

      submissionData.append("company_id", compId);

      submissionData.append("service_catalog", JSON.stringify(formData.serviceCatalog));
      submissionData.append("geographic_reach", JSON.stringify(formData.geographicReach));
      submissionData.append("employee_count", formData.employeeCount);
      submissionData.append("qms_status", formData.qmsStatus);
      submissionData.append("compliance_statement", formData.complianceStatement);
      submissionData.append("past_audit_date", formData.pastAuditDate);
      submissionData.append("sop_following", formData.sopFollowing);

      // ✅ Existing QMS file
      submissionData.append(
        "existingQmsFile",
        existingQmsDoc.length ? existingQmsDoc[0].file : ""
      );

      // ✅ NEW QMS file — MUST MATCH multer: .single("qmsFile")
      if (newQmsFile) submissionData.append("qmsFile", newQmsFile);


      let res;

      if (isEdit) {
        res = await axios.put(
          `${BASE_API_URL}/api/seller/service/cro/update/${serviceId}`,
          submissionData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } else {
        res = await axios.post(
          `${BASE_API_URL}/api/seller/service/register/cro/${compId}`,
          submissionData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }

      if (res.data.status === "ok") setShowRgstrdScsModal(true);

    } catch (err) {
      console.error(err);
      alert("Error submitting CRO service.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>

      {/* ✅ Service Catalog */}
      <h6 className='mb-3 fw-medium'>Service Catalog *</h6>
      <Stack direction="row" gap={1} flexWrap="wrap" className='chipField mb-4'>
        {serviceCatalogOptions.map(option => (
          <Chip
            key={option}
            label={option}
            variant={formData.serviceCatalog.includes(option) ? 'filled' : 'outlined'}
            color={formData.serviceCatalog.includes(option) ? 'warning' : 'default'}
            onClick={() => toggleServiceCatalog(option)}
            size="large"
          />
        ))}
      </Stack>

      {formData.serviceCatalog.includes("Other") && (
        <Box mt={3}>
          <Stack direction="row" gap={1}>
            <TextField
              label="Enter Custom Service"
              value={customService}
              onChange={(e) => setCustomService(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustomCatalogService();
                }
              }}
            />
            <Button variant="contained" color='warning' onClick={addCustomCatalogService}>
              Add
            </Button>
          </Stack>
        </Box>
      )}

      {/* ✅ Geographic Reach */}
      <div className="row mt-4">
        <div className='col-md-6'>
          <h6 className='mb-3 fw-medium'>Geographic Reach *</h6>
          <Autocomplete
            multiple
            options={countryList}
            value={formData.geographicReach}
            onChange={(_, value) => setFormData(prev => ({ ...prev, geographicReach: value }))}
            renderInput={(params) => (
              <TextField {...params} label="Select Countries" />
            )}
          />
        </div>

        <div className='col-md-6'>
          <h6 className='mb-3 fw-medium'>Employees</h6>
          <TextField
            fullWidth
            type='number'
            label='No. of Employees'
            value={formData.employeeCount}
            onChange={(e) => setFormData(prev => ({ ...prev, employeeCount: e.target.value }))}
          />
        </div>
      </div>

      {/* ✅ QMS */}
      <h4 className='mt-5 pt-4 mb-3 fw-medium'>Regulatory & Quality Compliance</h4>

      <h6 className='mb-3 fw-medium'>QMS Documentation *</h6>
      <RadioGroup
        row
        value={formData.qmsStatus}
        onChange={(e) => setFormData(prev => ({ ...prev, qmsStatus: e.target.value }))}
      >
        <FormControlLabel value='yes' control={<Radio />} label='Yes' />
        <FormControlLabel value='no' control={<Radio />} label='No' />
      </RadioGroup>

      {formData.qmsStatus === "yes" && (
        <Box mt={2}>
          {/* ✅ Existing file preview */}
          {existingQmsDoc.length > 0 && !newQmsFile && (
            <Box mb={2}>
              <Typography>Existing QMS Document:</Typography>
              <Link
                href={`${BASE_API_URL}/uploads/user_cro_docs/${existingQmsDoc[0].file}`}
                target="_blank"
              >
                View Document
              </Link>
            </Box>
          )}

          {/* ✅ Upload new file */}
          <Button variant="outlined" component="label" color="warning">
            {existingQmsDoc.length && !newQmsFile
              ? "Replace QMS Document"
              : "Upload QMS Document"
            }
            <input type="file" accept="application/pdf" hidden onChange={handleQmsFileChange} />
          </Button>

          {newQmsFile && (
            <Typography mt={1}>{newQmsFile.name}</Typography>
          )}
        </Box>
      )}

      <h6 className='mb-3 mt-4 fw-medium'>Compliance Statement</h6>
      <TextareaAutosize
        minRows={4}
        style={{ width: "100%", padding: 10 }}
        value={formData.complianceStatement}
        onChange={(e) => setFormData(prev => ({ ...prev, complianceStatement: e.target.value }))}
      />

      {/* ✅ Past audit & SOP */}
      <div className="row">
        <div className="col-md-4">
          <h6 className='mb-3 mt-4 fw-medium'>Past Audit Date</h6>
          <TextField
            type='date'
            fullWidth
            value={formData.pastAuditDate}
            onChange={(e) => setFormData(prev => ({ ...prev, pastAuditDate: e.target.value }))}
            inputProps={{
              max: new Date().toISOString().split("T")[0] // disables future dates
            }}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <h6 className='mb-3 mt-4 fw-medium'>Following SOPs *</h6>
          <RadioGroup
            row
            value={formData.sopFollowing}
            onChange={(e) => setFormData(prev => ({ ...prev, sopFollowing: e.target.value }))}
          >
            <FormControlLabel value='yes' control={<Radio />} label='Yes' />
            <FormControlLabel value='no' control={<Radio />} label='No' />
          </RadioGroup>
        </div>
      </div>

      {/* ✅ Submit */}
      <Button
        variant="contained"
        color="warning"
        onClick={handleSubmit}
        sx={{ mt: 4 }}
        className='w-100 py-3 shadow-none'
        disabled={loading}
      >
        {isEdit ? "Update CRO Service" : "Submit CRO Service"}
      </Button>

      <CompRgstrdFormSubmitted
        show={showRgstrdScsModal}
        onClose={() => setShowRgstrdScsModal(false)}
        redirectLink={`/user-company-details?compId=${compId}`}
      />
    </Wrapper>
  );
};

export default UserCROServiceRegistrationForm;

const Wrapper = styled(Box)`
  margin: 50px auto;
  #countriesSelector MuiChip-label{
    color: orange !important;
  }
  .MuiChip-root {
    cursor: pointer;
    padding: 10px;
    font-size: 14px;
    border-radius: 8px;
  }
  .MuiChip-filled span {
    ${'' /* color: white !important; */}
  }
`;
